import { useCallback, useEffect } from 'react';
import { useLocaleState } from 'react-admin';
import { DEFAULT_LANGUAGE, isLanguageSupported } from './languages';

const LOCALE_STORAGE_KEY = 'locale';

const readPersistedLocale = () => {
    // 1. Locale zapisany przez aplikację
    try {
        const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (saved && isLanguageSupported(saved)) {
            return saved;
        }
    } catch (error) {
        console.warn('Unable to read locale from localStorage', error);
    }

    // 2. Fallback: język przeglądarki
    try {
        if (typeof globalThis !== 'undefined' && globalThis.navigator) {
            const navigatorLang = globalThis.navigator.language || globalThis.navigator.languages?.[0];
            if (navigatorLang) {
                const baseLang = navigatorLang.split('-')[0].toLowerCase();
                if (isLanguageSupported(baseLang)) {
                    return baseLang;
                }
            }
        }
    } catch (error) {
        console.warn('Unable to detect locale from browser', error);
    }

    // 3. Ostateczny fallback
    return DEFAULT_LANGUAGE;
};

const persistLocale = locale => {
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (error) {
        console.warn('Unable to persist locale to localStorage', error);
    }
};

export const usePersistedLocale = () => {
    const [locale, setLocale] = useLocaleState();

    useEffect(() => {
        const savedLocale = readPersistedLocale();
        if (savedLocale !== locale) {
            setLocale(savedLocale);
        }
    }, [locale, setLocale]);

    const setPersistedLocale = useCallback(
        nextLocale => {
            if (!isLanguageSupported(nextLocale) || nextLocale === locale) {
                return Promise.resolve(locale);
            }
            persistLocale(nextLocale);
            return setLocale(nextLocale);
        },
        [locale, setLocale],
    );

    return [locale, setPersistedLocale];
};

export const useTagNameSource = () => {
    const [currentLocale] = useLocaleState();

    return currentLocale?.toLowerCase().startsWith('en') ? 'nameEn' : 'name';
};

export const useTagCategoryNameSource = () => {
    const [currentLocale] = useLocaleState();

    return currentLocale?.toLowerCase().startsWith('en') ? 'categoryNameEn' : 'categoryName';
};
